import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Box, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { getTimeFormat } from '@utils/dateHelper';
import './SlotSelectionDialog.scss';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material/Select';

interface Plan {
  _id: string;
  name: string;
  price?: number;
}

interface SelectedSlots {
  start: Date | null;
  end: Date | null;
}

interface Stylist {
  _id: string;
  availability: {
    startHour?: string;
    endHour?: string;
    isDefaultClose?: boolean;
    timeSlots: Array<{
      _id: string;
      start: string;
      end: string;
      title: string;
      remark: string;
      isOpen: boolean;
    }>;
    leaveDates: string[];
    dayoff: number[];
  };
}

interface SlotFormData {
  title?: string;
  remark?: string;
  customerName?: string;
  customerPhone?: string;
  planId?: string;
  originalAmount?: number | string;
  discountedAmount?: number | string;
}

interface SlotDetail {
  isOpen: boolean;
  selectedOption: string;
  title: string | undefined;
  remark: string | undefined;
  reservationData: SlotFormData;
  start: Date | null;
  end: Date | null;
}

export interface SlotSelectionDialogProps {
  selectedSlots: SelectedSlots;
  plans: Plan[];
  onSave: (detail: SlotDetail) => void;
  onClose: () => void;
  stylist: Stylist;
}

const SlotSelectionDialog = ({ selectedSlots, plans, onSave, onClose, stylist }: SlotSelectionDialogProps) => {
  const [open, setOpen] = useState(true);
  const [selectedOption, setSelectedOption] = useState(stylist.availability.isDefaultClose ? 'open' : 'block');
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<SlotFormData>();
  const { t } = useTranslation();

  const handleClose = () => {
    setOpen(false);
    reset();
    onClose();
  };

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
    reset();
  };

  const onSubmit = (data: SlotFormData) => {
    onSave({
      isOpen: selectedOption === 'open',
      selectedOption,
      title: data.title,
      remark: data.remark,
      reservationData: data,
      start: selectedSlots.start,
      end: selectedSlots.end,
    });
    handleClose();
  };

  const renderForm = () => {
    switch(selectedOption) {
      case 'block':
        return (
          <Box component="form">
            <FormControl fullWidth className="form-field">
              <TextField
                label={t('reservation.title')}
                id="title"
                {...register("title")}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </FormControl>
            <FormControl fullWidth className="form-field">
              <TextField
                label={t('reservation.remark')}
                id="remark"
                multiline
                rows={2}
                {...register("remark")}
              />
            </FormControl>
          </Box>
        );
      case 'reserve':
        return (
          <Box component="form">
            <FormControl fullWidth className="form-field">
              <TextField
                label={t('reservation.customerName')}
                id="customerName"
                {...register("customerName", {
                  required: t('validation.customerNameRequired')
                })}
                error={!!errors.customerName}
                helperText={errors.customerName?.message}
              />
            </FormControl>
            <FormControl fullWidth className="form-field">
              <TextField
                label={t('reservation.phoneNumber')}
                id="customerPhone"
                {...register("customerPhone")}
                error={!!errors.customerPhone}
                helperText={errors.customerPhone?.message}
              />
            </FormControl>
            <FormControl fullWidth className="form-field">
              <InputLabel htmlFor="plan">{t('reservation.plan')}</InputLabel>
              <Select
                id="plan"
                label={t('reservation.plan')}
                {...register("planId", {
                  required: t('validation.planRequired')
                })}
                error={!!errors.planId}
                defaultValue=""
                onChange={(e: SelectChangeEvent) => {
                  const selectedPlan = plans.find(plan => plan._id === e.target.value);
                  setValue('originalAmount', selectedPlan ? selectedPlan.price : '');
                  setValue('discountedAmount', selectedPlan ? selectedPlan.price : '');
                }}
              >
                {plans.map((plan, index) => (
                  <MenuItem key={index} value={plan._id}>
                    {plan.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="error">
                {errors.planId?.message}
              </Typography>
            </FormControl>

            <FormControl fullWidth className="form-field">
              <TextField
                label={t('reservation.originalAmount')}
                id="originalAmount"
                {...register("originalAmount")}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </FormControl>

            <FormControl fullWidth className="form-field">
              <TextField
                label={t('reservation.discountedAmount')}
                id="discountedAmount"
                {...register("discountedAmount")}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </FormControl>

            <FormControl fullWidth className="form-field">
              <TextField
                label={t('reservation.remark')}
                id="remark"
                multiline
                rows={2}
                {...register("remark")}
              />
            </FormControl>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>{t('reservation.manageTimeSlot')}</DialogTitle>
      <DialogContent className="dialog-content">
        <Typography variant="subtitle1" gutterBottom>
          {t('reservation.selectedTimeSlots')}: {selectedSlots.start ? getTimeFormat(selectedSlots.start) : ''} - {selectedSlots.end ? getTimeFormat(selectedSlots.end) : ''}
        </Typography>

        <Box className="options-container" sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          {stylist.availability.isDefaultClose && (
            <Box className={`option-card ${selectedOption === 'open' ? 'selected' : ''}`}
              onClick={() => handleOptionSelect('open')}>
              <Typography variant="subtitle2">{t('reservation.openTimeSlot')}</Typography>
            </Box>
          )}

          <Box className={`option-card ${selectedOption === 'block' ? 'selected' : ''}`}
            onClick={() => handleOptionSelect('block')}>
            <Typography variant="subtitle2">{t('reservation.blockTimeSlot')}</Typography>
          </Box>

          <Box className={`option-card ${selectedOption === 'reserve' ? 'selected' : ''}`}
            onClick={() => handleOptionSelect('reserve')}>
            <Typography variant="subtitle2">{t('reservation.reserveTimeSlot')}</Typography>
          </Box>
        </Box>

        {renderForm()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={!selectedOption}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SlotSelectionDialog;
